import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
    Stack,
    Typography,
    Card,
    SxProps,
    TableRowProps,
    CardContent,
    Box,
    TableContainerProps,
    TableProps,
    TableCellProps,
    TableBodyProps,
} from '@mui/material';

import {
    ContactName,
    CurrencyAmount,
    DefaultCell,
    DonationId,
    DonationPayments,
    DonationType,
    DonorPriority,
    DonorStatus,
    Emails,
    ExpenseId,
    ItemContact,
    Phones,
    Relationship,
} from './Cells';
import Section from '@Components/components/Section';
import { get, merge } from 'lodash';
import Sort from './Components/Sort';

const cellComponents = {
    // display_name: ContactName,
    // relationship: Relationship,
    // email_addresses: Emails,
    // phone_numbers: Phones,
    // donation_id: DonationId,
    // donation_type: DonationType,
    // donation_payments: DonationPayments,
    // expense_id: ExpenseId,
    // 'donor.priority': DonorPriority,
    // 'donor.status': DonorStatus,
};

const defaultComponents = {
    item_contact: ItemContact,
    currency_amount: CurrencyAmount,
};

function DynamicCell({
    row,
    field,
    Component,
    componentType,
    renderField,
    rowCellProps,
    ...rest
}) {
    if (renderField)
        return <TableCell {...rowCellProps}>{renderField(row)}</TableCell>;

    const Cell =
        Component ||
        (componentType
            ? defaultComponents?.[componentType]
            : cellComponents?.[field]) ||
        DefaultCell;

    return (
        <TableCell {...rowCellProps}>
            {<Cell row={row} field={field} {...rest} />}
        </TableCell>
    );
}

// TODO: truncate
// TODO: donation_status

type RowType = Record<string, any>;

export interface ColumnType {
    name: string;
    field: string;
    sort?: string;
    Component?: React.ComponentType<any>;
    componentType?: string;
    renderField?: (row: RowType) => React.ReactNode;
    headCellProps?: TableCellProps;
    rowCellProps?: TableCellProps;
}

interface DynamicTableProps {
    columns: ColumnType[];
    rows: RowType[];
    extraRows?: React.ReactNode;
    checkField?: string;
    checkedRows?: RowType[];
    setCheckedRows?: (rows: RowType[]) => void;
    toggleCheck?: (row: RowType) => void;
    getSort?: () => string;
    onSortChange?: (sort: SortType) => void;
    rowProps?: TableRowProps | ((row: RowType, index: number) => TableRowProps);
    containerProps?: TableContainerProps;
    tableProps?: TableProps;
    renderRow?: (
        row: RowType,
        tableRowComponent: JSX.Element,
        index: number,
    ) => React.ReactNode;
    tableHeader?: React.ReactNode;
    tableFooter?: React.ReactNode;
    TableBodyComponent?: React.ComponentType<any>;
    tableBodyProps?: TableBodyProps;
    noData?: React.ReactNode;
}

export const DynamicTable = ({
    columns = [],
    rows,
    extraRows,
    tableHeader,
    tableFooter,
    TableBodyComponent = TableBody,
    tableBodyProps,
    checkField,
    checkedRows,
    setCheckedRows,
    toggleCheck,
    getSort,
    onSortChange,
    rowProps,
    containerProps,
    noData,
    tableProps,
    renderRow,
}: DynamicTableProps) => {
    const handleCheck = (e: React.ChangeEvent<HTMLInputElement>, row) => {
        e.stopPropagation();
        const shiftKey = e.nativeEvent.shiftKey;
        const rowKey = get(row, checkField);
        if (shiftKey && !checkedRows.includes(rowKey)) {
            const startIndex = rows.findIndex(
                r => get(r, checkField) === rowKey,
            );
            const endIndex = rows.findIndex(
                r => get(r, checkField) === checkedRows.at(-1),
            );

            const start = Math.min(startIndex, endIndex);
            const end = Math.max(startIndex, endIndex);
            const newChecked = rows
                .slice(start, end + 1)
                .map(r => get(r, checkField))
                .filter(r => !checkedRows.includes(r));
            setCheckedRows([...checkedRows, ...newChecked]);
            return;
        }
        toggleCheck(get(row, checkField));
    };

    const handleCheckAll = () => {
        if (checkedRows.length === rows.length) setCheckedRows([]);
        else setCheckedRows(rows.map(row => get(row, checkField)));
    };

    return (
        <TableContainer component={Card} {...containerProps}>
            <Box sx={{ m: 1.5 }}>{tableHeader}</Box>
            <CardContent sx={{ overflowX: 'auto', m: 1.5 }}>
                <Table {...tableProps}>
                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: 'surfaceContainer.main',
                                '& .MuiTableCell-root': {
                                    paddingY: 0,
                                    // borderBottom: 0,
                                },
                                '& .MuiTableCell-root:first-of-type': {
                                    borderTopLeftRadius: 12,
                                    borderBottomLeftRadius: 12,
                                },
                                '& .MuiTableCell-root:last-child': {
                                    borderTopRightRadius: 12,
                                    borderBottomRightRadius: 12,
                                },
                            }}
                        >
                            {columns.map(({ headCellProps, ...col }, index) => (
                                <TableCell {...headCellProps} key={index}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        {col?.checkbox && (
                                            <Checkbox
                                                indeterminate={
                                                    checkedRows.length > 0 &&
                                                    checkedRows.length <
                                                        rows.length
                                                }
                                                checked={
                                                    checkedRows.length &&
                                                    checkedRows.length ===
                                                        rows.length
                                                }
                                                onChange={handleCheckAll}
                                            />
                                        )}
                                        <Typography
                                            variant="inherit"
                                            paddingY={2}
                                        >
                                            {col.name}
                                        </Typography>
                                        {col.sortField && (
                                            <Sort
                                                field={col.sortField}
                                                value={getSort(col.sortField)}
                                                onChange={onSortChange}
                                            />
                                        )}
                                    </Stack>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBodyComponent {...tableBodyProps}>
                        {rows.map((row, index) => {
                            const props =
                                typeof rowProps === 'function'
                                    ? rowProps(row, index)
                                    : rowProps;
                            const checked = checkedRows?.includes(
                                row[checkField],
                            );
                            const tableRowComponent = (
                                <TableRow
                                    key={index}
                                    {...merge(
                                        {
                                            hover: true,
                                            sx: {
                                                cursor: props?.onClick
                                                    ? 'pointer'
                                                    : 'default',
                                                '&:hover': {
                                                    '.row-hover': {
                                                        display: 'flex',
                                                    },
                                                    '.row-not-hover': {
                                                        display: 'none',
                                                    },
                                                },
                                                ...(checked && {
                                                    backgroundColor:
                                                        'action.selected',
                                                }),
                                                '&:last-child td, &:last-child th':
                                                    {
                                                        ':first-of-type': {
                                                            borderBottomLeftRadius: 20,
                                                        },
                                                        ':last-child': {
                                                            borderBottomRightRadius: 20,
                                                        },
                                                    },
                                                '&:first-of-type td, &:first-of-type th':
                                                    {
                                                        ':first-of-type': {
                                                            borderTopLeftRadius: 20,
                                                        },
                                                        ':last-child': {
                                                            borderTopRightRadius: 20,
                                                        },
                                                    },
                                            },
                                        },
                                        props,
                                    )}
                                >
                                    {/* {checkField && (
                                        <TableCell>
                                            <Checkbox
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                                onChange={e =>
                                                    handleCheck(e, row)
                                                }
                                                checked={checkedRows.includes(
                                                    row[checkField],
                                                )}
                                            />
                                        </TableCell>
                                    )} */}
                                    {columns.map((col, index) => (
                                        <DynamicCell
                                            key={index}
                                            row={row}
                                            checked={checked}
                                            handleCheck={e =>
                                                handleCheck(e, row)
                                            }
                                            {...col}
                                        />
                                    ))}
                                </TableRow>
                            );

                            return renderRow
                                ? renderRow(row, tableRowComponent, index)
                                : tableRowComponent;
                        })}
                        {extraRows}
                    </TableBodyComponent>
                </Table>
            </CardContent>
            {!rows.length && noData && <Box sx={{ m: 3 }}>{noData}</Box>}
            {tableFooter && <Box sx={{ m: 1.5 }}>{tableFooter}</Box>}
        </TableContainer>
    );
};
